import {
  Box,
  Text,
  ModalContent,
  Image,
  IconButton,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { LoanContext } from "providers/loan";
import _ from "underscore";
import { getDecimalNumber, numberToDollar, stringToFullNumber } from "utils/helpers";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { supabase } from "index";
import { AlertsContext } from "providers/alerts";
import moment from "moment";
import {
  getCustomCurrency,
  getFundingDate,
  getLoanType,
  getSidebarBackground,
  pullInterestRate,
  pullNoramlInterestRate,
} from "utils/uiconfighelper";
import { getInterestRateCalRwa } from "utils/permissionHelper";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { uploadStatement } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";
import { useQueryClient } from "@tanstack/react-query";

interface InterestStatamentPopupContentProps {
  singleStatement: any;
  printref?: any;
  onClose: () => void;
}

const InterestStatamentPopupContent: React.FC<InterestStatamentPopupContentProps> = ({
  singleStatement,
  printref,
}) => {
  const queryClient = useQueryClient();
  const { loan } = useContext(LoanContext);
  const { user } = useTenant();
  const { showError } = useContext(AlertsContext);
  const [, setBudget] = useState(0);
  const [items, setItems] = useState<any[]>([]);
  const [, setSections] = useState<{
    [key: number]: any[];
  }>({});
  const [amortizationData, setAmortizationData] = useState<any[]>([]);
  const [, setLoading] = useState(true);

  useEffect(() => {
    const groupedSections = _.groupBy(items, item => item.section);
    setSections(groupedSections);
    const newBudget = items.reduce((prev, curr) => (prev += curr.amount), 0);
    setBudget(newBudget);
  }, [items]);

  const getInterestData = useCallback(async () => {
    const { data: amortizationData, error: amortizationError } = await supabase.rpc(
      "getinterestamttable",
      {
        loan_id: loan!.id,
      }
    );

    if (amortizationError) {
      showError({
        title: "Amortization Supabase API Error",
        description: getErrorMessage(amortizationError),
      });
      return;
    }
    setAmortizationData(amortizationData);
  }, [loan, showError]);

  const currentMonthPayment = () => {
    if (amortizationData.length > 0) {
      const currentDate = new Date().toISOString().slice(0, 7);
      const currentMonthData = amortizationData.filter((item: any) => {
        const data = item.installment_date.slice(0, 7);
        if (data === currentDate) {
          return item;
        }
      });
      if (currentMonthData) {
        const amount = currentMonthData[0].installmentamt;
        return `${numberToDollar(getDecimalNumber(amount), getCustomCurrency(user))}`;
      }
    } else {
      return "Loading";
    }
  };

  const currentMonthRemainingPrinciple = () => {
    if (amortizationData.length > 0) {
      const currentDate = new Date().toISOString().slice(0, 7);
      const currentMonthData = amortizationData.filter((item: any) => {
        const data = item.installment_date.slice(0, 7);
        if (data === currentDate) {
          return item;
        }
      });
      if (currentMonthData) {
        const amount = currentMonthData[0].Closing_balance;
        return `${numberToDollar(getDecimalNumber(amount), getCustomCurrency(user))}`;
      }
    } else {
      return "Loading";
    }
  };

  useEffect(() => {
    const newBudget = items.reduce((prev: any, curr: any) => (prev += curr.amount), 0);
    setBudget(newBudget);
    setItems(loan!.draw_requests);
  }, [items, loan]);

  useEffect(() => {
    getInterestData();
  }, [getInterestData]);

  const handlePrint = () => {
    const modalContentElement = printref.current;
    if (modalContentElement) {
      modalContentElement.classList.add("print-mode");
      window.print();
      modalContentElement.classList.remove("print-mode");
    }
  };

  const updateBase64PDFStatement = useCallback(async () => {
    setLoading(true);
    const modalContentElement = printref.current;
    if (modalContentElement) {
      html2canvas(modalContentElement, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      })
        .then(async canvas => {
          modalContentElement.classList.add("print-mode");
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF();
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
          pdf.addImage(imgData, "PNG", 0, 0, imgWidth * ratio, imgHeight * ratio);
          // pdf.save("download.pdf");
          // Move the await call inside the async function
          try {
            const generatedPDF = await pdf.output("blob");
            await uploadStatement({
              file: generatedPDF,
              currentStatementId: singleStatement.id,
            });
          } catch (error) {
            console.error("Error uploading PDF:", error);
          } finally {
            modalContentElement.classList.remove("print-mode");
            setLoading(false);
            queryClient.invalidateQueries({
              queryKey: ["statements", loan!.id],
            });
          }
        })
        .catch(error => {
          console.error("Error capturing canvas:", error);
        });
    } else {
      console.error("Element with id 'print-mode' not found.");
    }
  }, [singleStatement, printref]);

  useEffect(() => {
    if (singleStatement !== "") {
      setTimeout(() => {
        updateBase64PDFStatement();
      }, 3000);
    }
  }, [singleStatement, updateBase64PDFStatement]);

  return (
    <ModalContent
      minW="70%"
      borderRadius={"15px"}
      ref={printref}
      className="print-mode add-an-width"
    >
      <Box
        display={"flex"}
        marginTop={"10px"}
        borderBottomWidth="1px"
        height={"50px"}
        justifyContent="space-between"
      >
        <Text color={"black"} marginLeft={"33px"}>
          Interest Statements
        </Text>
        <Box display="flex">
          <IconButton
            className="hide-on-print"
            aria-label="Download"
            icon={<DownloadIcon />}
            position={"relative"}
            right={"40px"}
            bottom={"4px"}
            color="black"
            background="white"
            borderRadius="50%"
            _hover={{ color: "#ffffff", background: "black" }}
            onClick={() => handlePrint()}
          />
          <Box marginRight={"10px"} marginLeft={"10px"}>
            <ModalCloseButton marginTop={"5px"} fontSize={"15px"} className="hide-on-print" />
          </Box>
        </Box>
      </Box>

      <Box height={"20px"} marginTop={"20px"} marginLeft={"34px"}>
        <Image
          src={loan!.lender_company.logo ?? ""}
          alt="company-logo"
          background={getSidebarBackground(user)}
          width={"200px"}
        />
      </Box>

      <Box display={"flex"} marginTop={"20px"} justifyContent="space-between">
        <Box marginLeft={"34px"} marginTop={"25px"}>
          <Text color={"black"}>
            {" "}
            {loan!.location?.formatted ? loan!.location?.formatted : "Not Provided"}
          </Text>
        </Box>

        <Box alignItems="end" px="10">
          <Text color={"black"}>Loan Number : {`#${loan?.id.split("-")[0]}`}</Text>
          <Text color={"black"}>Monthly Payments : </Text>
          <Text
            fontSize={"30px"}
            color={"black"}
            fontWeight={"600"}
            marginLeft={"35px"}
            marginRight={"14px"}
          >
            {currentMonthPayment()}
          </Text>
        </Box>
      </Box>

      <Box display={"flex"} marginTop={"15px"} marginLeft={"30px"} marginRight={"15px"}>
        <Box width={"50%"}>
          <Box borderBottomWidth="1px" fontWeight={"650"}>
            Loan items
          </Box>
          <Box display={"flex"} flexDirection={"column"}>
            <Box display={"flex"} justifyContent="space-between">
              <Text color={"black"} display={"flex"} my={0.8}>
                Loan amount
              </Text>
              <Text color={"black"} my={0.8}>
                {numberToDollar(stringToFullNumber(loan!.amount), getCustomCurrency(user))}
              </Text>
            </Box>
            <Box display={"flex"} justifyContent="space-between">
              <Text color={"black"} display={"flex"} my={0.8}>
                Interest rate {getInterestRateCalRwa(user) ? "(Fixed)" : `(${getLoanType(loan)})`}{" "}
              </Text>
              <Text color={"black"} my={0.8}>
                {getInterestRateCalRwa(user)
                  ? pullNoramlInterestRate(loan)
                  : pullInterestRate(loan)}
                %
              </Text>
            </Box>
            <Box display={"flex"} justifyContent="space-between">
              <Text color={"black"} display={"flex"} my={0.8}>
                Loan term :{" "}
              </Text>
              <Text color={"black"} my={0.8}>
                {Number(loan!.duration) + Number(loan!.holiday_period)}
              </Text>
            </Box>
          </Box>
        </Box>

        <Box width={"50%"} marginLeft={"35px"} marginRight={"20px"}>
          <Box borderBottomWidth="1px" fontWeight={"650"}>
            Loan summary
          </Box>
          <Box display={"flex"} justifyContent="space-between">
            <Text color={"black"} my={0.8}>
              Funding date :
            </Text>
            <Text color={"black"} my={0.8}>
              {moment(getFundingDate(loan)).format("YYYY-MM-DD")}
            </Text>
          </Box>
          <Box display={"flex"} justifyContent="space-between">
            <Text color={"black"} display={"flex"} my={0.8}>
              {" "}
              Remaining principal :{" "}
            </Text>
            <Text color={"black"} my={0.8}>
              {currentMonthRemainingPrinciple()}
            </Text>
          </Box>
        </Box>
      </Box>

      <Box px="10" marginTop={"15px"} marginBottom={"30px"}>
        <Box>
          <Text
            background="#5A5A5A"
            color={"#ffffff"}
            height={"40px"}
            marginTop={"5px"}
            paddingTop={"6px"}
            paddingLeft={"12px"}
            borderRadius={"5px"}
            marginRight={"20px"}
          >
            Amortization schedule
          </Text>
        </Box>

        <Box maxHeight="50%" marginTop="10px" marginRight={"22px"} mb="50px">
          <TableContainer marginTop={"10px"}>
            <Table variant="simple" border={"1px"}>
              <Thead>
                <Tr border={"1px"}>
                  <Th border={"1px"} textAlign="center">
                    Month
                  </Th>
                  <Th border={"1px"} textAlign="center">
                    Total Due
                  </Th>
                  <Th border={"1px"} textAlign="center">
                    Interest
                  </Th>
                  <Th border={"1px"} textAlign="center">
                    Balance
                  </Th>
                  <Th border={"1px"} textAlign="center">
                    Schedule Payment Date
                  </Th>
                </Tr>
              </Thead>
              <Tbody border={"1px"}>
                {/* Generate rows for each month from 1 to 12 */}
                {amortizationData.map((item: any, index: number) => {
                  return (
                    <Tr key={index} border={"1px"}>
                      <Td border="1px" textAlign="center">
                        {item.installmentno}
                      </Td>
                      <Td border="1px" textAlign="right">
                        {numberToDollar(
                          getDecimalNumber(item.installmentamt),
                          getCustomCurrency(user)
                        )}
                      </Td>
                      <Td border="1px" textAlign="right">
                        {numberToDollar(
                          getDecimalNumber(item.interestamt),
                          getCustomCurrency(user)
                        )}
                      </Td>
                      <Td border="1px" textAlign="right">
                        {numberToDollar(
                          getDecimalNumber(item.Closing_balance),
                          getCustomCurrency(user)
                        )}
                      </Td>
                      <Td border="1px" textAlign="center">
                        {item.installment_date}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </ModalContent>
  );
};

export default InterestStatamentPopupContent;
